import Signature from "../../modules/Signature";
import SignatureLogo from "../../modules/SignatureLogo";
import classes from "./Home.module.css";
import { motion, useScroll, useTransform } from "framer-motion";
import { useViewportSize } from "@mantine/hooks";
import About from "src/modules/About";
import Experience from "src/modules/Experience";
import { FaAnglesDown } from "react-icons/fa6";
import SectionHeader from "src/components/SectionHeader";
import anime from "animejs";
import { useContext, useEffect, useRef } from "react";
import Education from "src/modules/Education";
import Skills from "src/modules/Skills";
import Projects from "src/modules/Projects";
import { AppContext } from "src/api/AppContext";
import Contact from "src/modules/Contact";

const Home = () => {
  const { height, width } = useViewportSize();
  const { scrollY } = useScroll();
  const { animTrigger } = useContext(AppContext);

  const sigWidth = useTransform(
    scrollY,
    [0, height / 2 - 75],
    [width - 15, 150],
    {
      clamp: true,
    }
  );

  const opacity = useTransform(scrollY, [0, 1], [1, 0]);

  const opacityLogo = useTransform(
    scrollY,
    [0, 1, height / 2 - 37, height / 2 - 36],
    [0, 1, 1, 0]
  );

  const animateDDArrow = () => {
    anime({
      targets: "#page-indicator",
      opacity: [0, 1],
      easing: "easeInOutSine",
      duration: 800,
      direction: "alternate",
      loop: true,
    });
  };

  useEffect(() => {
    if (animTrigger === undefined) {
      setTimeout(() => {
        animateDDArrow();
      }, 3200);
    }
  }, [animTrigger]);

  // Hero scroll-snap: the first viewport (hero) behaves as an all-or-nothing
  // pane. Any scroll gesture that starts while the hero is partially in view
  // snaps to About (scrolling down) or back to the top (scrolling up). Once
  // About is reached, scrolling is free again.
  const snappingRef = useRef(false);
  useEffect(() => {
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const getAboutTop = () => {
      const el = document.getElementById("about-me");
      return el
        ? window.scrollY + el.getBoundingClientRect().top - 90
        : window.innerHeight;
    };

    const snapTo = (target: number) => {
      snappingRef.current = true;
      if (prefersReduced) {
        window.scrollTo(0, target);
        snappingRef.current = false;
        return;
      }
      const startY = window.scrollY;
      const dist = target - startY;
      const duration = 550;
      const startT = performance.now();
      // easeInOutQuad
      const ease = (t: number) =>
        t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      const step = (now: number) => {
        const p = Math.min(1, (now - startT) / duration);
        window.scrollTo(0, startY + dist * ease(p));
        if (p < 1) {
          requestAnimationFrame(step);
        } else {
          snappingRef.current = false;
        }
      };
      requestAnimationFrame(step);
    };

    // Wheel events closer together than this are treated as one continuous
    // gesture (including trackpad inertia); a larger gap starts a new gesture.
    const GESTURE_GAP = 250;
    let lastWheelTs = 0;

    const onWheel = (e: WheelEvent) => {
      if (snappingRef.current) {
        e.preventDefault();
        return;
      }

      const rest = getAboutTop();
      const y = window.scrollY;
      const now = performance.now();
      const isNewGesture = now - lastWheelTs > GESTURE_GAP;
      lastWheelTs = now;

      // Hero zone (above the About rest point): all-or-nothing. A down gesture
      // snaps to About, an up gesture snaps to the very top.
      if (y < rest - 2) {
        e.preventDefault();
        snapTo(e.deltaY > 0 ? rest : 0);
        return;
      }

      // Scrolling up at/below the About rest point.
      if (e.deltaY < 0) {
        const atRest = y <= rest + 2;

        // Only a *new* up gesture started while already resting at About
        // snaps up into the hero.
        if (atRest && isNewGesture) {
          e.preventDefault();
          snapTo(0);
          return;
        }

        // Otherwise hold the barrier: an up-scroll (or its inertia) that would
        // cross above About is clamped to the rest point instead of drifting
        // into the hero.
        const nextY = y + e.deltaY; // deltaY < 0 → moving up
        if (atRest || nextY <= rest) {
          e.preventDefault();
          window.scrollTo(0, rest);
          return;
        }
      }

      // Below the rest point (scrolling down, or an up-scroll still far from the
      // barrier): free scrolling.
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.6 } }}
      exit={{ opacity: 0, transition: { duration: 0.6 } }}
    >
      <div className={classes["sig-section"]}>
        <h1 className={classes["visually-hidden"]}>
          Andrew Tanaka — Platform Engineering Manager &amp; Architect
        </h1>
        <motion.div style={{ width: sigWidth, opacity: opacityLogo }}>
          <SignatureLogo />
        </motion.div>
        <motion.div style={{ width: sigWidth, opacity }}>
          <Signature />
        </motion.div>
        <motion.div style={{ opacity }} className={classes["hero-caption"]}>
          Software Engineer · Platform Architect
        </motion.div>
      </div>
      <motion.div
        style={{ opacity }}
        className={classes["page-down-indicator"]}
      >
        <FaAnglesDown id={"page-indicator"} />
      </motion.div>
      <div className={classes["section"]} id={"about-me"}>
        <div>
          <SectionHeader title={"About Me"} />
          <About />
        </div>
      </div>
      <div className={classes["section"]} id={"experience"}>
        <div>
          <SectionHeader title={"Experience"} />
          <Experience />
        </div>
      </div>
      <div className={classes["section"]} id={"education"}>
        <div>
          <SectionHeader title={"Education"} />
          <Education />
        </div>
      </div>
      <div className={classes["section"]} id={"skills"}>
        <div>
          <SectionHeader title={"Skills"} />
          <Skills />
        </div>
      </div>
      <div className={classes["section"]} id={"projects"}>
        <div>
          <SectionHeader title={"Projects"} />
          <Projects />
        </div>
      </div>
      <div className={classes["section"]} id={"contact-me"}>
        <div>
          <SectionHeader title={"Contact Me"} />
          <Contact />
        </div>
      </div>
    </motion.div>
  );
};

export default Home;
